/**
 * A2 - how the alert is actually sent.
 *
 * Drawn in native elements, not an image: the left spine is the pipeline this
 * repository contains, the right side is the national infrastructure it hands
 * the document to, and every arrow between them carries the protocol that
 * carries the message and the latency budgeted for that hop.
 *
 * The latency on each outward arrow is the same number the dispatch simulation
 * uses for that channel, so the diagram and the timeline cannot disagree.
 *
 * The two kinds of box look different on purpose: solid border and accent rule
 * for what is built here, dashed border and dim text for what is not ours.
 */

import type { Architecture, PipelineStage, Route, RouteHop } from './shared'
import { C } from '../../design/tokens'

const BUILT_BORDER = '1px solid var(--ink-accent-dim)'
const EXTERNAL_BORDER = '1px dashed var(--ink-border-strong)'

function Box({
  label,
  sublabel,
  built,
  width,
}: {
  label: string
  sublabel: string
  built: boolean
  width?: number
}) {
  return (
    <div
      style={{
        width,
        minWidth: 0,
        background: built ? 'var(--ink-surface-raised)' : 'transparent',
        border: built ? BUILT_BORDER : EXTERNAL_BORDER,
        borderLeft: built ? '3px solid var(--ink-accent)' : EXTERNAL_BORDER,
        borderRadius: 'var(--r-control)',
        padding: '6px 10px',
      }}
    >
      <div
        style={{
          fontSize: 'var(--fs-cell)',
          lineHeight: 1.25,
          color: built ? 'var(--ink-text)' : 'var(--ink-text-dim)',
          fontWeight: built ? 600 : 500,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 'var(--fs-label)', lineHeight: 1.3, color: 'var(--ink-text-faint)' }}>
        {sublabel}
      </div>
    </div>
  )
}

/** The vertical arrow between two pipeline stages, carrying its own label. */
function SpineArrow({ protocol, latency }: { protocol: string; latency: string }) {
  return (
    <div className="flex items-center gap-2" style={{ paddingLeft: 14 }}>
      <svg width="10" height="26" aria-hidden="true">
        <line x1="5" y1="0" x2="5" y2="19" stroke="var(--ink-accent-dim)" strokeWidth="1.5" />
        <path d="M5 26 L1.5 19 L8.5 19 Z" fill="var(--ink-accent-dim)" />
      </svg>
      <span style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
        {protocol}
        <span style={{ color: 'var(--ink-text-faint)' }}> · {latency}</span>
      </span>
    </div>
  )
}

/** A horizontal arrow with the protocol above it and the latency below. */
function RouteArrow({ protocol, latency, w = 130 }: { protocol: string; latency?: string; w?: number }) {
  return (
    <div className="flex shrink-0 flex-col items-center justify-center" style={{ width: w }}>
      <span
        className="w-full text-center"
        style={{ fontSize: 'var(--fs-label)', lineHeight: 1.25, color: 'var(--ink-text-dim)' }}
      >
        {protocol}
      </span>
      <svg width={w - 4} height="9" aria-hidden="true">
        <line x1="0" y1="4.5" x2={w - 12} y2="4.5" stroke={C.borderStrong} strokeWidth="1" />
        <path d={`M${w - 4} 4.5 L${w - 12} 1 L${w - 12} 8 Z`} fill={C.borderStrong} />
      </svg>
      <span
        className="font-mono"
        style={{ fontSize: 'var(--fs-label)', lineHeight: 1.25, color: 'var(--ink-text-faint)' }}
      >
        {latency ?? ''}
      </span>
    </div>
  )
}

function RouteRow({ route }: { route: Route }) {
  return (
    <div className="flex items-center" style={{ minHeight: 52 }}>
      <RouteArrow protocol={route.protocol} latency={route.latency} />
      <Box label={route.label} sublabel={route.sublabel} built={false} width={180} />
      {route.hops.map((hop: RouteHop) => (
        <div key={hop.id} className="flex items-center">
          <RouteArrow protocol={hop.protocol} w={106} />
          <Box label={hop.label} sublabel={hop.sublabel} built={false} width={150} />
        </div>
      ))}
    </div>
  )
}

export function ArchitectureDiagram({ data }: { data: Architecture }) {
  const spine = data.pipeline
  const gatewayIndex = spine.findIndex((s: PipelineStage) => s.id === 'gateway')
  const before = spine.slice(0, gatewayIndex + 1)
  const after = spine.slice(gatewayIndex + 1)
  const returning = new Set(data.returns.map((r) => r.source))

  return (
    <section className="panel" data-testid="alert-architecture">
      <div className="mb-3 flex flex-wrap items-baseline gap-4">
        <div className="section-label">Alert dissemination chain</div>
        {data.legend.map((item) => (
          <span
            key={item.kind}
            className="flex items-center gap-2"
            style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 22,
                height: 11,
                borderRadius: 2,
                background: item.kind === 'built' ? 'var(--ink-surface-raised)' : 'transparent',
                border: item.kind === 'built' ? BUILT_BORDER : EXTERNAL_BORDER,
                borderLeft:
                  item.kind === 'built' ? '3px solid var(--ink-accent)' : EXTERNAL_BORDER,
              }}
            />
            {item.label}
          </span>
        ))}
      </div>

      <div className="overflow-x-auto">
        <div className="flex gap-4" style={{ minWidth: 1076 }}>
          {/* The spine: what this repository does with the result. */}
          <div className="shrink-0" style={{ width: 238 }}>
            {before.map((stage, index) => (
              <div key={stage.id}>
                <Box label={stage.label} sublabel={stage.sublabel} built />
                {index < before.length - 1 && stage.edge_protocol && (
                  <SpineArrow
                    protocol={stage.edge_protocol}
                    latency={stage.edge_latency ?? ''}
                  />
                )}
              </div>
            ))}
            <div style={{ paddingLeft: 14, height: 14 }}>
              <svg width="10" height="14" aria-hidden="true">
                <line
                  x1="5"
                  y1="0"
                  x2="5"
                  y2="14"
                  stroke="var(--ink-accent-dim)"
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                />
              </svg>
            </div>
            {after.map((stage, index) => (
              <div key={stage.id}>
                <Box label={stage.label} sublabel={stage.sublabel} built />
                {index < after.length - 1 && stage.edge_protocol && (
                  <SpineArrow
                    protocol={stage.edge_protocol}
                    latency={stage.edge_latency ?? ''}
                  />
                )}
              </div>
            ))}
          </div>

          {/* The fan-out: what it is handed to. */}
          <div className="flex-1">
            <div
              style={{
                borderLeft: '1px solid var(--ink-border)',
                paddingLeft: 2,
              }}
            >
              {data.routes.map((route) => (
                <RouteRow key={route.id} route={route} />
              ))}
            </div>
            <div
              className="mt-3"
              style={{ borderTop: '1px solid var(--ink-border)', paddingTop: 8 }}
            >
              <div className="section-label mb-1">
                Confirmations returning to the tracker · {returning.size} of{' '}
                {data.routes.length} routes
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                {data.returns.map((r) => (
                  <span
                    key={r.source}
                    style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}
                  >
                    {data.routes.find((x) => x.id === r.source)?.label ?? r.source}
                    <span style={{ color: 'var(--ink-text-faint)' }}> · {r.protocol}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
